// Standalone verification page for the in-app file viewer.
//
// Renders BlockMediaPreview with synthetic photo + attachment payloads
// (inline base64 data URLs — no auth, no blob fetch) so a headless
// browser can click the tiles and confirm the lightbox actually opens
// inline rather than navigating to a blank page.
//
// Only the verification harness ever loads this route. Linked nowhere
// in the app — reach it by typing /verify-lightbox in the address bar.

import { BlockMediaPreview } from '@/components/app/BlockMediaPreview';
import type { LessonStep } from '@/lib/app-store';

// Smallest valid PNG: 4×4 red square, base64'd inline.
const TINY_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAFklEQVR4nGP8z8AARMQAJqIUAYUYAQBkkgGB87cZUgAAAABJRU5ErkJggg==';

// Smallest valid PDF: 1 page, blank — base64'd inline.
const TINY_PDF =
  'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iajw8L1R5cGUvQ2F0YWxvZy9QYWdlcyAyIDAgUj4+ZW5kb2JqCjIgMCBvYmo8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PmVuZG9iagozIDAgb2JqPDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgMjAwIDIwMF0+PmVuZG9iagp4cmVmCjAgNAowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMDkgMDAwMDAgbiAKMDAwMDAwMDA1NCAwMDAwMCBuIAowMDAwMDAwMDk5IDAwMDAwIG4gCnRyYWlsZXI8PC9TaXplIDQvUm9vdCAxIDAgUj4+CnN0YXJ0eHJlZgoxNTYKJSVFT0YK';

const SYNTHETIC_STEP_PHOTO: LessonStep = {
  id: 'verify-step-photo',
  kind: 'worksheet',
  minutes: 10,
  title: 'Verify photo lightbox',
  detail: 'Photo tile below should open an in-app modal on click.',
  photo: {
    name: 'red-square.png',
    dataUrl: TINY_PNG,
  },
};

const SYNTHETIC_STEP_FILE: LessonStep = {
  id: 'verify-step-file',
  kind: 'worksheet',
  minutes: 10,
  title: 'Verify attachment lightbox',
  detail: 'Attachment tile below should open an in-app PDF viewer on click.',
  attachment: {
    name: 'blank.pdf',
    type: 'application/pdf',
    size: 303,
    dataUrl: TINY_PDF,
  },
};

export default function VerifyLightbox() {
  return (
    <div className="min-h-screen bg-background paper-dots p-8">
      <div className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Lightbox verification</h1>
          <p className="text-sm text-muted-foreground">
            Click the photo or the attachment&apos;s View (eye) button. The viewer should open
            an in-app modal. NOT navigate the tab.
          </p>
        </div>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Photo tile (admin mode)
          </h2>
          <BlockMediaPreview step={SYNTHETIC_STEP_PHOTO} mode="admin" />
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Attachment tile (admin mode)
          </h2>
          <BlockMediaPreview step={SYNTHETIC_STEP_FILE} mode="admin" />
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Photo tile (cover mode, view only, no download)
          </h2>
          <BlockMediaPreview step={SYNTHETIC_STEP_PHOTO} mode="cover" />
        </section>
      </div>
    </div>
  );
}
